/**
 * The `ai-agent-runs` custom-method seam.
 *
 * `ai-agent-runs` is registered on the API with
 * `methods: ['find','get','create','start','cancel']` — `start` and `cancel` are
 * Feathers 5 CUSTOM methods, which a socket.io client does not get for free.
 * The general mechanism (and the wire shape it relies on) lives in
 * aiAgentMethods.ts; this file is the typed, memoized handle the chat store
 * uses for the two run verbs.
 *
 * TIMEOUT: `start` runs the whole agent loop (LLM turns + tool dispatch) before
 * its socket ack, which can take tens of seconds. HQ's socket is configured with
 * no `ackTimeout`, so the promise is genuinely open-ended. It is deliberately NOT
 * routed through `ServiceStore.boundedServiceCall`, whose bound would abort a
 * legitimate long run.
 */
import { getAgentMethods, type AgentMethodsApp } from "./aiAgentMethods";

export type RunActionsApp = AgentMethodsApp;

export interface AgentRunActions {
  /** Run the agentic loop for a pending run. Resolves when the run settles. */
  start(runId: string): Promise<unknown>;
  /** Best-effort cancel. No-op server-side if the run already reached a terminal state. */
  cancel(runId: string): Promise<unknown>;
}

/**
 * One handle per Feathers client. Keyed weakly so a torn-down RootStore (tests,
 * hot reload) does not pin its client in memory.
 */
const handles = new WeakMap<object, AgentRunActions | null>();

const buildRunActions = (app: RunActionsApp): AgentRunActions | null => {
  const handle = getAgentMethods(app, "ai-agent-runs", "start", "cancel");
  if (!handle) return null;
  // The run id travels as the DATA argument — see aiAgentMethods.ts.
  return {
    start: (runId: string) => handle.call("start", runId, {}),
    cancel: (runId: string) => handle.call("cancel", runId, {}),
  };
};

/**
 * Resolve (and memoize) the `start`/`cancel` action handle for a Feathers client.
 * Returns null when the client has no socket transport.
 */
export const getRunActions = (
  app: RunActionsApp | null | undefined,
): AgentRunActions | null => {
  if (!app) return null;
  const key = app as unknown as object;
  if (handles.has(key)) return handles.get(key) ?? null;
  let built: AgentRunActions | null = null;
  try {
    built = buildRunActions(app);
  } catch (err) {
    console.warn("ai-agent: could not attach run action methods", err);
    built = null;
  }
  handles.set(key, built);
  return built;
};

/** Test seam: drop the memoized handle for a client. */
export const resetRunActions = (
  app: RunActionsApp | null | undefined,
): void => {
  if (app) handles.delete(app as unknown as object);
};
